//missionstatement.js
import React from 'react';
import '../index.css';
import './fullmanifesto.css';
import '../output.css';

import ScrollWipe from './fullscrollwiper';
import ScrollWipeGroup from "./scrollwipegroup";

import BorrowingImg from "../assets/images/occupy.png";
import LendingImg from "../assets/images/americanahouse.png";
import EnforcementImg from "../assets/images/angrytrader.png";

import { useState, useEffect } from 'react';
import { useRef, useLayoutEffect } from "react";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < breakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
}

function useFitToWidth(ref, { min = 44, max = 140 } = {}) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.tagName !== "H1") return;

    const fit = () => {
      if (window.innerWidth < 769) {
        el.style.fontSize = "";
        return;
      }

      const available = el.getBoundingClientRect().width;
      if (!available) return;

      let lo = min, hi = max, best = min;

      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        el.style.fontSize = `${mid}px`;

        if (el.scrollWidth <= available) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      el.style.fontSize = `${best}px`;
    };

    const run = () => requestAnimationFrame(fit);

    let cancelled = false;
    const start = async () => {
      if (document.fonts?.ready) await document.fonts.ready;
      if (cancelled) return;
      run();
    };
    start();

    const ro = new ResizeObserver(run);
    ro.observe(el);
    window.addEventListener("resize", run);

    return () => {
      cancelled = true;
      ro.disconnect();
      window.removeEventListener("resize", run);
    };
  }, [ref, min, max]);
}

export default function MissionStatement(){
  const isMobile = useIsMobile();

  const h1Ref = useRef(null);
  const h2Ref = useRef(null);

  useFitToWidth(h1Ref, { min: 56, max: 120 });
  useFitToWidth(h2Ref, { min: 56, max: 120 });

  if (isMobile) {
    return (
    <section className="hero" style={{ backgroundColor: "#5FBA8B", padding: "0 6vw" }}>
      <div style={{ maxWidth: "100%", paddingBottom: "60px" }}>

        <h1 className="hero__title" style={{ color: "#FFFFFF", fontSize: "36px", lineHeight: 1.2, paddingTop: "40px", paddingBottom: "24px" }}>
          The banks are stealing from you.
        </h1>

        <p style={{ color: "#FFFFFF", fontSize: "16px", lineHeight: 1.7, marginBottom: "24px" }}>
          The modern economy is built on a scam. You were taught to trust banks: to park your money in savings, let it compound, build a "future." You were told it would magically compound itself into generational wealth. While they generously give you 0.1% APY, the banks are earning 7%, 8%, 9% interest with your money. They get the bread while you get the crumbs. This is theft.
        </p>

        <p style={{ color: "#FFFFFF", fontSize: "16px", lineHeight: 1.7, marginBottom: "24px" }}>
          If you are familiar with deposit banking, then you know the model of a bank is something like this: a bank takes your money and lends it out to Bobby for 7% interest. As a reward, the bank pays you 0.1% interest. The bank used your money and kept 90% of the profits. That's not very fair. But what if we could get rid of the bank?
        </p>

        <p style={{ color: "#FFFFFF", fontSize: "16px", lineHeight: 1.7, marginBottom: "24px" }}>
          This is the foundational idea behind commonwealth. If you could eliminate the middleman, Bobby could pay a lower interest rate while you earn higher yields. Instead of Bobby paying 7% to a bank that pays you 0.1%, Bobby could just pay you 4% directly. That's a win-win for you and Bobby. That's exactly what we're building at commonwealth.
        </p>

        <p style={{ color: "#FFFFFF", fontSize: "16px", lineHeight: 1.7, marginBottom: "24px" }}>
          So why doesn't this exist? The big reason is trust. People need to trust that they will get their money back, or the entire system falls apart. That's why we have banks. Banks have the legal coercive power to enforce loans; they can damage your credit, they can repossess your house, garnish your wages, etc. Banks provide trust.
        </p>

        <p style={{ color: "#FFFFFF", fontSize: "16px", lineHeight: 1.7, marginBottom: "24px" }}>
          Except we live in 2026. And the technology exists to build trustless systems. The vision we have for commonwealth, years from now, is ultimately to tokenize the entire mortgage process. Crypto is going to take yield away from the banks and return it to the people. Crypto is going to make it so that loans are cheaper and accessible to everybody.
        </p>

        <p style={{ color: "#FFFFFF", fontSize: "16px", lineHeight: 1.7, marginBottom: "48px" }}>
          We believe in this vision, and that's what we're building at commonwealth.
        </p>

        {/* Three problems */}
        <div style={{ marginBottom: "48px" }}>
          <div style={{ color: "#FFFFFF", fontSize: "13px", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "12px", opacity: 0.7 }}>
            The way we see it
          </div>
          <h2 style={{ color: "#FFFFFF", fontSize: "28px", fontWeight: "bold", lineHeight: 1.2, marginBottom: "32px" }}>
            Commonwealth solves three problems.
          </h2>

          {[
            {
              num: "1",
              headline: "The banks are stealing from you.",
              body: "Banks use your money, do little of value, and keep the lion's share of the profits from lending. Both lenders and borrowers would be better off without the middleman."
            },
            {
              num: "2",
              headline: "1.4 billion people on Earth are unbanked.",
              body: "These people do not have access to a bank, or do not use one. Even in the United States, millions of households remain outside the banking system entirely."
            },
            {
              num: "3",
              headline: "We need to accelerate the mass adoption of crypto.",
              body: "Crypto can help strip power from the banks, return yield to the public, and expand financial freedom. The technology is too important to waste on speculation and unserious products."
            }
          ].map(({ num, headline, body }) => (
            <div key={num} style={{ display: "flex", gap: "16px", marginBottom: "28px" }}>
              <div style={{ color: "#FFFFFF", fontSize: "32px", fontWeight: "bold", opacity: 0.3, minWidth: "32px", lineHeight: 1 }}>
                {num}
              </div>
              <div>
                <h3 style={{ color: "#FFFFFF", fontSize: "18px", fontWeight: "bold", marginBottom: "8px" }}>{headline}</h3>
                <p style={{ color: "#FFFFFF", fontSize: "15px", lineHeight: 1.6, opacity: 0.85 }}>{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div style={{ marginBottom: "48px" }}>
          <div style={{ color: "#FFFFFF", fontSize: "13px", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "12px", opacity: 0.7 }}>
            How Commonwealth works
          </div>
          <h2 style={{ color: "#FFFFFF", fontSize: "28px", fontWeight: "bold", lineHeight: 1.2, marginBottom: "32px" }}>
            A better model for borrowing, lending, and enforcement.
          </h2>

          {/* A / Borrowing */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ color: "#FFFFFF", fontSize: "13px", letterSpacing: "0.06em", opacity: 0.7, marginBottom: "8px" }}>A / Borrowing</div>
            <h3 style={{ color: "#FFFFFF", fontSize: "22px", fontWeight: "bold", marginBottom: "16px" }}>Borrowing begins with underwriting.</h3>
            <img src={BorrowingImg} alt="Borrowing process" style={{ width: "100%", borderRadius: "12px", marginBottom: "16px" }} />
            <ul style={{ color: "#FFFFFF", fontSize: "15px", lineHeight: 1.7, paddingLeft: "20px", opacity: 0.9 }}>
              <li style={{ marginBottom: "8px" }}>Imagine Bobby applies for a loan.</li>
              <li style={{ marginBottom: "8px" }}>We assess Bobby's creditworthiness, income, and other relevant factors, then assign a risk score and expected interest rate.</li>
              <li style={{ marginBottom: "8px" }}>His loan is pooled with 15–30 similar loans to create a loan pool — a bundle of similar mortgages grouped together.</li>
              <li>These loan pools are then listed on the public marketplace, where they are available for depositors to fund them.</li>
            </ul>
          </div>

          {/* B / Lending */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ color: "#FFFFFF", fontSize: "13px", letterSpacing: "0.06em", opacity: 0.7, marginBottom: "8px" }}>B / Lending</div>
            <h3 style={{ color: "#FFFFFF", fontSize: "22px", fontWeight: "bold", marginBottom: "16px" }}>Lenders fund pools and earn yield.</h3>
            <img src={LendingImg} alt="Lending process" style={{ width: "100%", borderRadius: "12px", marginBottom: "16px" }} />
            <div style={{ color: "#FFFFFF", fontSize: "15px", lineHeight: 1.7, opacity: 0.9 }}>
              <p style={{ marginBottom: "8px" }}>Micah has extra money and wants yield. He browses Commonwealth for new loan pools.</p>
              <p style={{ marginBottom: "8px" }}>He finds a pool and commits capital to it. Hundreds of other users do the same.</p>
              <p style={{ marginBottom: "8px" }}>If enough money is pledged into the pool, funds are released to the borrowers so they can buy homes.</p>
              <p style={{ marginBottom: "8px" }}>After funding a pool, Micah receives ERC-20 tokens representing his ownership in the underlying loans.</p>
              <p>He can either hold those tokens and collect monthly payments or sell them early for liquidity.</p>
            </div>
          </div>

          {/* C / Enforcement */}
          <div style={{ marginBottom: "40px" }}>
            <div style={{ color: "#FFFFFF", fontSize: "13px", letterSpacing: "0.06em", opacity: 0.7, marginBottom: "8px" }}>C / Enforcement</div>
            <h3 style={{ color: "#FFFFFF", fontSize: "22px", fontWeight: "bold", marginBottom: "16px" }}>Until code can enforce loans, Commonwealth does.</h3>
            <img src={EnforcementImg} alt="Enforcement protocols" style={{ width: "100%", borderRadius: "12px", marginBottom: "16px" }} />
            <ul style={{ color: "#FFFFFF", fontSize: "15px", lineHeight: 1.7, paddingLeft: "20px", opacity: 0.9 }}>
              <li style={{ marginBottom: "8px" }}>When a loan is issued, Commonwealth either takes the lien on the house or partners with a servicer to hold it.</li>
              <li style={{ marginBottom: "8px" }}>That lien gives Commonwealth the legal power to repossess or otherwise control the property if enforcement becomes necessary.</li>
              <li style={{ marginBottom: "8px" }}>Commonwealth can also report performance to credit bureaus.</li>
              <li style={{ marginBottom: "8px" }}>Lenders can request foreclosure or similar actions when borrowers default.</li>
              <li>Until enforcement can be fully automated onchain, Commonwealth handles enforcement on behalf of depositors.</li>
            </ul>
          </div>
        </div>

        {/* Final copy */}
        <p style={{ color: "#FFFFFF", fontSize: "16px", lineHeight: 1.8, marginBottom: "40px", opacity: 0.9 }}>
          For too long, the banks have taken advantage of you.<br />
          We started commonwealth to take back the American Dream.<br />
          Commonwealth increases savings yield & lowers interest rates. Both of these things strengthen the middle class and pose an existential threat to banks.<br />
          The banks want to trap you in their cycle of 0.1% yield, stealing from you and offering you crumbs.<br />
          We're building Commonwealth because it's time for a better way forward. One built beyond banks.<br />
          Join commonwealth, because
        </p>

        <h1 style={{ color: "#FFFFFF", fontSize: "36px", fontWeight: "bold", lineHeight: 1.2, paddingBottom: "60px" }}>
          the revolution will be tokenized.
        </h1>

      </div>
    </section>
    )
  } else {

  return (
    <section className="hero" style={{backgroundColor: "#5FBA8B"}}>
      <div className="hero__inner">

        {/* HEADER stays OUTSIDE group so it doesn't get tokenized */}
        <h1 ref={h2Ref} className="hero__title" style={{color:"#FFFFFF"}}>
          The banks are stealing from you.
        </h1>

        <ScrollWipeGroup seam={0.5}>
          <ScrollWipe as="p" className="hero__lede">
            The modern economy is built on a scam. You were taught to trust banks: to park your money in savings, let it compound, build a "future." You were told it would magically compound itself into generational wealth. While they generously give you 0.1% APY, the banks are earning 7%, 8%, 9% interest with your money. They get the bread while you get the crumbs. This is theft.
          </ScrollWipe>

          <ScrollWipe as="p" className="hero__rede">
            If you are familiar with deposit banking, then you know the model of a bank is something like this: a bank takes your money and lends it out to Bobby for 7% interest. As a reward, the bank pays you 0.1% interest. The bank used your money and kept 90% of the profits. That's not very fair. But what if we could get rid of the bank?
          </ScrollWipe>

          <ScrollWipe as="p" className="hero__lede">
            This is the foundational idea behind commonwealth. If you could eliminate the middleman, Bobby could pay a lower interest rate while you earn higher yields. Instead of Bobby paying 7% to a bank that pays you 0.1%, Bobby could just pay you 4% directly. That's a win-win for you and Bobby. That's exactly what we're building at commonwealth. A trustless, peer-to-peer lending market so that you never have to use a bank again.
          </ScrollWipe>

          <ScrollWipe as="p" className="hero__rede">
            So why doesn't this exist? The big reason is trust. People need to trust that they will get their money back, or the entire system falls apart. That's why we have banks. Banks have the legal coercive power to enforce loans; they can damage your credit, they can repossess your house, garnish your wages, etc. Banks provide trust.
          </ScrollWipe>

          <ScrollWipe as="p" className="hero__lede">
            Except we live in 2026. And the technology exists to build trustless systems. The vision we have for commonwealth, years from now, is ultimately to tokenize the entire mortgage process. Crypto is going to allow us to automate the billions of dollars and millions of hours spent in admistrative busywork to support mortgage loans. Crypto is going to take yield away from the banks and return it to the people. Crypto is going to make it so that loans are cheaper and accesible to everybody.
          </ScrollWipe>
          <ScrollWipe as="p" className="hero__rede">
          We believe in this vision, and that's what we're building at commonwealth.
          </ScrollWipe>
                    </ScrollWipeGroup>

          {/* If you want <br/> and lists, use as="div" (valid HTML) */}
          <ScrollWipe as="section" className="manifestoProblems">
  <div className="manifestoProblems__intro">The way we see it</div>
  <h2 className="manifestoProblems__title">
    Commonwealth solves three problems.
  </h2>

  <div className="manifestoProblems__list">
    <article className="manifestoProblemRow">
      <div className="manifestoProblemRow__num">1</div>
      <div className="manifestoProblemRow__content">
        <h3 className="manifestoProblemRow__headline">
          The banks are stealing from you.
        </h3>
        <p className="manifestoProblemRow__body">
          Banks use your money, do little of value, and keep the lion’s share of
          the profits from lending. Both lenders and borrowers would be better
          off without the middleman.
        </p>
      </div>
    </article>

    <article className="manifestoProblemRow">
      <div className="manifestoProblemRow__num">2</div>
      <div className="manifestoProblemRow__content">
        <h3 className="manifestoProblemRow__headline">
          1.4 billion people on Earth are unbanked.
        </h3>
        <p className="manifestoProblemRow__body">
          These people do not have access to a bank, or do not use one. Even in
          the United States, millions of households remain outside the banking
          system entirely.
        </p>
      </div>
    </article>

    <article className="manifestoProblemRow">
      <div className="manifestoProblemRow__num">3</div>
      <div className="manifestoProblemRow__content">
        <h3 className="manifestoProblemRow__headline">
          We need to accelerate the mass adoption of crypto.
        </h3>
        <p className="manifestoProblemRow__body">
          Crypto can help strip power from the banks, return yield to the
          public, and expand financial freedom. The technology is too important
          to waste on speculation and unserious products.
        </p>
      </div>
    </article>
  </div>
</ScrollWipe>




  <div className="manifestoProcess__eyebrow">How Commonwealth works</div>
  <h2 className="manifestoProcess__title">
    A better model for borrowing, lending, and enforcement.
  </h2>

  <div className="manifestoProcess__stack">
    <article className="manifestoStep manifestoStep--featured">
      <div className="manifestoStep__main">
        <div className="manifestoStep__meta">A / Borrowing</div>
        <h3 className="manifestoStep__title">Borrowing begins with underwriting.</h3>

        <ul className="manifestoStep__listRight">
          <li>Imagine Bobby applies for a loan.</li>
          <li>
            We assess Bobby's creditworthiness, income, and other relevant factors,
            then assign a risk score and expected interest rate.
          </li>
          <li>
            Her loan is pooled with 15–30 similar loans to create a loan pool —
            a bundle of similar mortgages grouped together.
          </li>
          <li>
            These loan pools are then listed on the public marketplace, where they are available for depositors to fund them.
          </li>
        </ul>
      </div>

      <div className="manifestoStep__media">
        <img
          src={BorrowingImg}
          alt="Borrowing process"
          className="manifestoStep__image"
        />
      </div>
    </article>

  <div className="manifestoProcess__stack">
    <article className="manifestoStep manifestoStep--split manifestoStep--splitLeft">
      <div className="manifestoStep__media">
        <img
          src={LendingImg}
          alt="Lending process"
          className="manifestoStep__image"
        />
      </div>

      <div className="manifestoStep__main">
        <div className="manifestoStep__meta">B / Lending</div>
        <h3 className="manifestoStep__title">Lenders fund pools and earn yield.</h3>

        <div className="manifestoStep__body">
          <p>Micah has extra money and wants yield. He browses Commonwealth for new loan pools.</p>
          <p>He finds a pool and commits capital to it. Hundreds of other users do the same.</p>
          <p>If enough money is pledged into the pool, funds are released to the borrowers so they can buy homes.</p>
          <p>After funding a pool, Micah receives ERC-20 tokens representing his ownership in the underlying loans.</p>
          <p>He can either hold those tokens and collect monthly payments or sell them early for liquidity.</p>
        </div>
      </div>
    </article>
    </div>


    <div className="manifestoProcess__stack">
      <article className="manifestoStep manifestoStep--featured">
        <div className="manifestoStep__main">
          <div className="manifestoStep__meta">C / Enforcement</div>
          <h3 className="manifestoStep__title">Until code can enforce loans, Commonwealth does.</h3>

          <ul className="manifestoStep__listRight">
          <li>
            When a loan is issued, Commonwealth either takes the lien on the house
            or partners with a servicer to hold it.
          </li>
          <li>
            That lien gives Commonwealth the legal power to repossess or otherwise
            control the property if enforcement becomes necessary.
          </li>
          <li>
            Commonwealth can also report performance to credit bureaus.
          </li>
          <li>
            Lenders can request foreclosure or similar actions when borrowers default.
          </li>
          <li>
            In short, until enforcement can be fully automated onchain, Commonwealth
            handles enforcement on behalf of depositors.
          </li>
          </ul>
        </div>

        <div className="manifestoStep__media">
          <img
            src={EnforcementImg}
            alt="Enforcement protocols"
            className="manifestoStep__image"
          />
        </div>
      </article>
    </div>
  </div>

<p className="parafinal">
          For too long, the banks have taken advantage of you. <br/>
          We started commonwealth to take back the American Dream. <br/>
          Comonwealth increases savings yield & lowers interest rates. Both of these things strengthen the middle class and pose an existential threat to banks. <br/>
          The banks  want to trap in their cycle of 0.1% yield, stealing from you and offering you crumbs. <br/>
          We're bulding Commonwealth because it's time for a better way forward. One built beyond banks. <br/>
          Join commonwealth, because
          </p>
        {/* CONCLUSION uses h2Ref (was incorrectly using h1Ref) */}
        <h1 ref={h2Ref} className="hero__conclusion" style={{color:"#FFFFFF", paddingTop:"50px"}}>
          the revolution will be tokenized.
        </h1>
      </div>
    </section>
  );
}
}
